import type { Mutation, Query } from "@tanstack/react-query";

/**
 * When this tab's most recent write started: the newest `submittedAt` among the
 * QueryClient's mutations, or `undefined` when it holds none (a write that did not
 * go through `useMutation`, such as a streamed request).
 *
 * The newest write, not the one the echo belongs to: a later start only makes
 * more queries count as stale, so a wrong guess costs a refetch, never a stale
 * screen.
 *
 * @param mutations - The QueryClient's mutation cache entries
 */
export function latestWriteStartedAt(
  mutations: readonly Pick<Mutation, "state">[],
): number | undefined {
  const startedAts = mutations
    .map((mutation) => mutation.state.submittedAt)
    .filter((submittedAt) => submittedAt > 0);
  return startedAts.length > 0 ? Math.max(...startedAts) : undefined;
}

/**
 * Whether an event that echoes this tab's own write should still refetch `query`.
 *
 * The mutation's `onSuccess` already refreshed what it named: those queries are
 * either fetching now or were updated after the write started, and fetching them
 * again is the double refresh the echo check exists to prevent. A query loaded
 * before the write — however recently — is neither, so the echo still refreshes
 * it and a forgotten key heals itself. With no recorded write to compare against,
 * every query that is not fetching refreshes.
 *
 * Pure: the write's start is a parameter.
 *
 * @param query - The cached query the event's refresh key matched
 * @param writeStartedAtMs - When this tab's latest write started (see `latestWriteStartedAt`)
 */
export function shouldRefreshOnOwnEcho(
  query: Pick<Query, "state">,
  writeStartedAtMs: number | undefined,
): boolean {
  if (query.state.fetchStatus === "fetching") return false;
  if (writeStartedAtMs === undefined) return true;
  return query.state.dataUpdatedAt < writeStartedAtMs;
}
